import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { Package, TrendingDown } from 'lucide-react';
import { Pagination } from '../../components/shared/Pagination';
import { usePagination } from '../../hooks/usePagination';

interface Movement {
  id: number;
  product_id: number;
  product_source_type: string;
  movement_type: string;
  qty: number;
  balance_after: number | null;
  reference_type: string;
  reference_id: string;
  notes: string;
  created_at: string;
  asin: string;
  product_title: string;
  product_image: string;
}

const MOVEMENT_COLORS: Record<string, string> = {
  purchase_received: 'text-green-600',
  po_received: 'text-green-600',
  amazon_received: 'text-green-700',
  report_incoming: 'text-teal-500',
  report_received: 'text-teal-600',
  report_in_transit: 'text-teal-400',
  report_cancelled: 'text-red-400',
  sale_allocated: 'text-orange-500',
  allocation_in: 'text-orange-400',
  sale_shipped: 'text-red-500',
  stock_allocated: 'text-orange-600',
  allocation_released: 'text-green-400',
  order_cancelled: 'text-red-400',
  adjustment: 'text-blue-500',
  return: 'text-purple-500',
  initial_load: 'text-cyan-500',
  physical_count: 'text-indigo-500',
  manual_adjustment: 'text-blue-500',
  recount: 'text-indigo-500',
  revert: 'text-gray-500',
};

interface Props {
  sellerId: number;
}

export default function InternalMovementsTab({ sellerId }: Props) {
  const { t } = useTranslation();
  const [movements, setMovements] = useState<Movement[]>([]);
  const [loading, setLoading] = useState(true);
  const pag = usePagination('purchases-internal-movements');

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({
      seller_id: String(sellerId),
      page: String(pag.page),
      per_page: String(pag.perPage),
      source_type: 'internal,mixed',
    });
    const res = await api.get<Movement[]>(`/inventory/movements?${params}`);
    if (res.success && res.data) {
      setMovements(res.data);
      pag.setTotal((res as any).meta?.total || 0);
    }
    setLoading(false);
  }, [sellerId, pag.page, pag.perPage]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { pag.resetPage(); }, [sellerId]);

  const fmtDateTime = (iso: string) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return <div className="text-center py-8 text-[var(--color-text-secondary)]">{t('common.loading')}…</div>;
  }

  if (movements.length === 0) {
    return (
      <div className="text-center py-12 text-[var(--color-text-secondary)]">
        <TrendingDown className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p>{t('inventory.noMovements')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto border border-[var(--color-border)] rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-[var(--color-bg-secondary)] border-b border-[var(--color-border)]">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('common.date')}</th>
              <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('catalog.product')}</th>
              <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('inventory.movementType')}</th>
              <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('inventory.qty')}</th>
              <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('inventory.balance')}</th>
              <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('inventory.reference')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--color-border)]">
            {movements.map(m => {
              const isPositive = m.qty > 0;
              const typeColor = MOVEMENT_COLORS[m.movement_type] || 'text-[var(--color-text)]';
              return (
                <tr key={m.id} className="hover:bg-[var(--color-bg-secondary)] transition-colors">
                  <td className="px-3 py-2 text-xs text-[var(--color-text-secondary)] whitespace-nowrap">
                    {fmtDateTime(m.created_at)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      {m.product_image ? (
                        <img src={m.product_image} alt="" className="w-8 h-8 rounded object-cover bg-white flex-shrink-0" />
                      ) : (
                        <div className="w-8 h-8 rounded bg-[var(--color-bg-secondary)] flex items-center justify-center flex-shrink-0">
                          <Package className="w-4 h-4 text-[var(--color-text-secondary)]" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-[var(--color-text)]">{m.product_title || m.asin}</p>
                        <p className="text-xs text-[var(--color-text-secondary)]">{m.asin}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-xs font-medium ${typeColor}`}>
                      {m.movement_type.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className={`font-bold ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
                      {isPositive ? '+' : ''}{m.qty}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-medium text-[var(--color-text)]">
                    {m.balance_after ?? '-'}
                  </td>
                  <td className="px-3 py-2 text-xs text-[var(--color-text-secondary)]">
                    <div className="flex items-center gap-1.5">
                      {m.reference_type === 'po' || m.reference_type === 'po_item' ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">SPO</span>
                      ) : m.reference_type === 'internal_stock' ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">STOCK</span>
                      ) : m.reference_type === 'ml_order' ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">ML</span>
                      ) : m.reference_type ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600 dark:bg-gray-800/40 dark:text-gray-400">{m.reference_type.replace(/_/g, ' ').toUpperCase().slice(0, 8)}</span>
                      ) : null}
                      <span>{m.reference_id || m.notes || '-'}</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination
        page={pag.page}
        perPage={pag.perPage}
        total={pag.total}
        totalPages={pag.totalPages}
        onPageChange={pag.setPage}
        onPerPageChange={pag.setPerPage}
      />
    </div>
  );
}
